import {
  format,
  differenceInMinutes,
  differenceInHours,
  differenceInDays,
  differenceInWeeks,
  differenceInMonths,
  differenceInYears,
  isYesterday,
  isThisWeek,
} from "date-fns";
import { ar, enUS } from "date-fns/locale";

/**
 * Function to format timestamps into readable date strings based on locale.
 *
 * @param {number} timestamp - The timestamp to format.
 * @param {string} localeStr - The locale string ('ar' or 'en').
 * @returns {string} - Formatted and localized date string.
 */
export const formatSidebarChatItemMessageTimeAgo = (timestamp: number, localeStr: string): string => {
  const date = new Date(timestamp);
  const now = new Date();
  const locale = localeStr === "ar" ? ar : enUS;

  const minutesDiff = differenceInMinutes(now, date);
  const hoursDiff = differenceInHours(now, date);
  const daysDiff = differenceInDays(now, date);
  const weeksDiff = differenceInWeeks(now, date);
  const monthsDiff = differenceInMonths(now, date);
  const yearsDiff = differenceInYears(now, date);

  // Less than a minute
  if (minutesDiff < 1) {
    return localeStr === "ar" ? "الآن" : "now";
  }

  // Less than an hour
  if (minutesDiff < 60) {
    return localeStr === "ar" ? `منذ ${minutesDiff} دقيقة` : `${minutesDiff} minute${minutesDiff > 1 ? "s" : ""} ago`;
  }

  // Less than 24 hours
  if (hoursDiff < 24) {
    return localeStr === "ar" ? `منذ ${hoursDiff} ساعة` : `${hoursDiff} hour${hoursDiff > 1 ? "s" : ""} ago`;
  }

  // Yesterday
  if (isYesterday(date)) {
    return localeStr === "ar" ? "الأمس" : "yesterday";
  }

  // This week (show day name)
  if (isThisWeek(date)) {
    return format(date, "EEEE", { locale });
  }

  // Less than a month (show days)
  if (daysDiff < 30) {
    return localeStr === "ar" ? `منذ ${daysDiff} يوم` : `${daysDiff} day${daysDiff > 1 ? "s" : ""} ago`;
  }

  // Less than a year (show months)
  if (monthsDiff < 12) {
    return localeStr === "ar" ? `منذ ${monthsDiff} شهر` : `${monthsDiff} month${monthsDiff > 1 ? "s" : ""} ago`;
  }

  // More than a year
  return localeStr === "ar" ? `منذ ${yearsDiff} سنة` : `${yearsDiff} year${yearsDiff > 1 ? "s" : ""} ago`;
};
